/**
 * Links between views. Backbone assembles every public URL (ADR 0011); the
 * Tool reads the view a `publicUrl` names and re-bases it onto the page it is
 * running on, so a staging page links to itself rather than to production,
 * and every link stays inside the router.
 */
import { buildUrl, parseUrl, type View } from "./router";

function viewOf(publicUrl: string | null): View | null {
  if (!publicUrl) return null;
  try {
    return parseUrl(new URL(publicUrl));
  } catch {
    return null;
  }
}

/** The Election view for a cycle, from its `publicUrl`, else its slug. */
export function electionHref(page: URL, cycle: { slug: string; publicUrl: string | null }, ballot: string | null = null): string {
  const view = viewOf(cycle.publicUrl);
  const slug = view?.kind === "election" ? view.cycle : cycle.slug;
  return buildUrl({ kind: "election", cycle: slug, ballot, race: null }, page);
}

/**
 * The Person view, carrying where the visitor came from so the page can offer
 * Return to Election. Null when Backbone gave no URL: render the name as text.
 */
export function personHref(
  page: URL,
  publicUrl: string | null,
  context: { from?: string | null; race?: string | null } = {}
): string | null {
  const view = viewOf(publicUrl);
  if (view?.kind !== "person") return null;
  return buildUrl({ kind: "person", person: view.person, from: context.from ?? null, race: context.race ?? null }, page);
}

/** The slug a Person `publicUrl` names, for matching Webflow profiles. */
export function personSlug(publicUrl: string | null): string | null {
  const view = viewOf(publicUrl);
  return view?.kind === "person" ? view.person : null;
}

export function directoryHref(page: URL): string {
  return buildUrl({ kind: "directory", filters: { q: "", year: null, status: null, jurisdiction: null } }, page);
}
